import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Button,
	Col,
	Descriptions,
	Divider,
	Form,
	Input,
	InputNumber,
	Modal,
	Row,
	Spin,
	message,
	theme,
} from "antd";
import * as API from "common/api";
import * as COMMONS from "common/common";
import { TapAnimationComponent } from "components";
import NumberStepper from "components/common/NumberStepper";
import { motion } from "framer-motion";
import { get } from "lodash";
import moment from "moment";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * @param {Object} props
 * @param {(props: { onOpen: void; onClose: void }) => JSX.Element} props.children
 * @param {number} props.registrationId
 * @returns {JSX.Element}
 */
export default function ReservationConfirmation({ children, registrationId }) {
	const colorPrimary = theme.useToken().token.colorPrimary;
	const [isOpen, setIsOpen] = useState(false);
	const navigate = useNavigate();
	const [form] = Form.useForm();
	const queryClient = useQueryClient();

	const onOpen = () => {
		setIsOpen(true);
	};

	const onClose = () => {
		setIsOpen(false);
	};

	const {
		data: {
			categoryTitle = "",
			occurrenceEndAt = "",
			occurrenceStartAt = "",
			occurrenceStartDate = "",
			checkAttended = "",
			telephone = "",
			lastNameKana = "",
			lastName = "",
			firstNameKana = "",
			firstName = "",
			displayName = "",
			customerRegistrationId2 = "",
			customerRegistrationId1 = "",
			registrationMessage = "",
			note = "",
			categoryGroupBooking,
			participantName = "",
			participantCount,
			companionCount,
			actualParticipantCount,
			actualCompanionCount,
			isManual,
		},
		isFetching: isFetchingReservationConfirmation,
		refetch: refetchReservationConfirmation,
		remove: removeReservationConfirmation,
	} = useQuery(
		[API.MUTATION_KEY_ADMIN_CONFIRM_REGISTRATION, registrationId],
		async () => {
			try {
				const { data } = await API.ADMIN_CONFIRM_REGISTRATION({
					registrationId,
				});
				return {
					categoryTitle:
						get(data, "Category.title") || get(data, "Occasion.title"),
					categoryGroupBooking:
						get(data, "Category.groupBooking") ||
						get(data, "Occasion.groupBooking"),
					occurrenceEndAt: get(data, "Occurrence.endAt"),
					occurrenceStartAt: get(data, "Occurrence.startAt"),
					occurrenceStartDate: get(data, "Occurrence.startDate"),
					checkAttended: get(data, "attended"),
					telephone: get(data, "Member.telephone"),
					lastNameKana: get(data, "Member.lastNameKana"),
					lastName: get(data, "Member.lastName"),
					firstNameKana: get(data, "Member.firstNameKana"),
					firstName: get(data, "Member.firstName"),
					displayName: get(data, "Member.displayName"),
					customerRegistrationId2: get(data, "Member.customerRegistrationId2"),
					customerRegistrationId1: get(data, "Member.customerRegistrationId1"),
					registrationMessage: get(data, "message"),
					note: get(data, "note"),
					isManual: get(data, "isManual"),
					participantName: get(data, "Member.displayName"),
					participantCount: get(data, "participantCount"),
					companionCount: get(data, "companionCount"),
					actualParticipantCount: get(data, "actualParticipantCount"),
					actualCompanionCount: get(data, "actualCompanionCount"),
				};
			} catch (error) {
				if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
					navigate(COMMONS.PERMISSION_ERROR_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
					message.warning({
						content: COMMONS.ERROR_SESSION_MSG,
						key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
					});
					navigate(COMMONS.ADMIN_LOGIN_ROUTE);
				} else if (
					error?.response?.status === COMMONS.RESPONSE_BAD_REQUEST_ERROR
				) {
					message.error({
						content: "QR is not valid",
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				} else {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				}
			}
		},
		{
			enabled: isOpen && !!registrationId,
			initialData: {},
		},
	);

	const registrationUpdateAttendedMutation = useMutation(async (data) => {
		try {
			await API.ADMIN_UPDATE_ATTENDED_REGISTRATION(data);

			message.success(COMMONS.SUCCESS_UPDATE_MSG);
			await refetchReservationConfirmation();
			queryClient.invalidateQueries({
				queryKey: [API.QUERY_KEY_ADMIN_OCCURRENCE_DETAIL],
			});
			queryClient.invalidateQueries({
				queryKey: [API.QUERY_KEY_ADMIN_CATEGORY_DETAIL],
			});
			queryClient.invalidateQueries({
				queryKey: [API.QUERY_KEY_ADMIN_OCCASION_DETAIL],
			});
		} catch (error) {
			if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
				navigate(COMMONS.PERMISSION_ERROR_ROUTE);
			} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
				message.warning({
					content: COMMONS.ERROR_SESSION_MSG,
					key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
				});
				navigate(COMMONS.ADMIN_LOGIN_ROUTE);
			} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
				message.error({
					content: COMMONS.ERROR_SYSTEM_MSG,
					key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
				});
			} else {
				message.error({
					content: COMMONS.ERROR_SYSTEM_MSG,
					key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
				});
			}
		}
	});

	const masterRegistrationConfirmationMutation = useMutation(
		[API.MUTATION_KEY_ADMIN_CONFIRM_REGISTRATION, registrationId],
		async (data) => {
			try {
				await API.masterRegistrationConfirmation({ registrationId, ...data });
				message.success(COMMONS.SUCCESS_UPDATE_MSG);
				await refetchReservationConfirmation();
				queryClient.invalidateQueries({
					queryKey: [API.QUERY_KEY_ADMIN_OCCURRENCE_DETAIL],
				});
				queryClient.invalidateQueries({
					queryKey: [API.QUERY_KEY_ADMIN_CATEGORY_DETAIL],
				});
				queryClient.invalidateQueries({
					queryKey: [API.QUERY_KEY_ADMIN_OCCASION_DETAIL],
				});
			} catch (error) {
				if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
					navigate(COMMONS.PERMISSION_ERROR_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
					message.warning({
						content: COMMONS.ERROR_SESSION_MSG,
						key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
					});
					navigate(COMMONS.ADMIN_LOGIN_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				} else {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				}
			}
		},
	);

	const handleUpdateAttended = () => {
		if (!registrationId) return;
		registrationUpdateAttendedMutation.mutate({ registrationId });
	};

	const handleFinishGroupBooking = (values) => {
		masterRegistrationConfirmationMutation.mutate(values);
		onClose();
	};

	return (
		<>
			{children({
				onOpen,
				onClose,
			})}

			<Modal
				open={isOpen}
				title="予約確認"
				onCancel={onClose}
				footer={null}
				centered
				destroyOnClose
				afterClose={() => {
					removeReservationConfirmation();
					form.resetFields();
				}}
			>
				{isFetchingReservationConfirmation ? (
					<div className="flex justify-center items-center h-20">
						<Spin />
					</div>
				) : categoryGroupBooking ? (
					<Form
						form={form}
						size="large"
						layout="vertical"
						initialValues={{
							participantName,
							participantCount,
							companionCount,
							actualParticipantCount,
							actualCompanionCount,
						}}
						onFinish={handleFinishGroupBooking}
					>
						<Form.Item label="名前" name="participantName">
							<Input disabled />
						</Form.Item>

						<Row>
							<Col span={12} className="text-center font-bold">
								予約
							</Col>
							<Col span={12} className="text-center font-bold">
								参加
							</Col>

							<Divider className="my-3" />

							<Col span={12}>
								<Form.Item
									label="参加者"
									name="participantCount"
									className="[&_.ant-form-item-control-input-content]:text-center"
								>
									<InputNumber readOnly className="[&_input]:text-center" />
								</Form.Item>
							</Col>

							<Col span={12}>
								<Form.Item
									label="参加者"
									name="actualParticipantCount"
									className="[&_.ant-form-item-control-input-content]:text-center"
								>
									<NumberStepper name="actualParticipantCount" />
								</Form.Item>
							</Col>

							<Col span={12}>
								<Form.Item
									label="同伴者"
									name="companionCount"
									className="[&_.ant-form-item-control-input-content]:text-center"
								>
									<InputNumber readOnly className="[&_input]:text-center" />
								</Form.Item>
							</Col>

							<Col span={12}>
								<Form.Item
									label="同伴者"
									name="actualCompanionCount"
									className="[&_.ant-form-item-control-input-content]:text-center"
								>
									<NumberStepper name="actualCompanionCount" />
								</Form.Item>
							</Col>
						</Row>

						<div className="flex justify-center gap-x-2 [&>button]:w-[75px]">
							<Button onClick={onClose}>閉じる</Button>
							<Button
								type="primary"
								htmlType="submit"
								loading={masterRegistrationConfirmationMutation.isLoading}
							>
								保存
							</Button>
						</div>
					</Form>
				) : (
					<motion.div
						variants={COMMONS.ANIMATION_VARIANT_STAGGER_CONTAINER}
						initial="hidden"
						animate="show"
						exit="hidden"
					>
						<motion.div variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}>
							<Descriptions
								column={1}
								labelStyle={{ width: "135px" }}
								contentStyle={{ padding: "0.5rem" }}
								bordered
							>
								{isManual ? (
									<Descriptions.Item label="氏名">
										<p className="text-sm">{`${lastName} ${firstName}`}</p>

										<p>{`(${lastNameKana} ${firstNameKana})`}</p>
									</Descriptions.Item>
								) : (
									<Descriptions.Item label="氏名">
										<p className="text-sm">{`${displayName}` || "ー"}</p>

										<p>{`${customerRegistrationId1}` || "ー"}</p>
									</Descriptions.Item>
								)}

								<Descriptions.Item label="電話番号">
									{isManual ? (
										<p className="text-sm">{telephone}</p>
									) : (
										<p className="text-sm">
											{`${customerRegistrationId2}` || "ー"}
										</p>
									)}
								</Descriptions.Item>

								<Descriptions.Item label={`${COMMONS.DEFAULT_SYSTEM_TYPE}`}>
									<p className="text-sm">{categoryTitle || "ー"}</p>
								</Descriptions.Item>

								<Descriptions.Item label="日時">
									<p className="text-sm">
										{occurrenceStartDate
											? moment(occurrenceStartDate).format(
													"YYYY年M月D日（ddd）",
												)
											: "ー"}
									</p>
									<p className="text-sm mt-2">
										<span
											className="inline-block rounded-full text-white px-2 mr-1 mb-1"
											style={{
												backgroundColor: colorPrimary,
											}}
										>
											{`${
												occurrenceStartAt
													? moment(occurrenceStartAt).format("HH:mm")
													: "ー"
											}
                      ～
                      ${
												occurrenceEndAt
													? moment(occurrenceEndAt).format("HH:mm")
													: "ー"
											}`}
										</span>
									</p>
								</Descriptions.Item>

								<Descriptions.Item label="メッセージ">
									<p className="text-sm whitespace-pre-wrap">
										{registrationMessage || "ー"}
									</p>
								</Descriptions.Item>

								{(note || []).map((item) => (
									<Descriptions.Item label={item?.label}>
										<p className="text-sm whitespace-pre-wrap">
											{item?.value || "ー"}
										</p>
									</Descriptions.Item>
								))}
							</Descriptions>

							<div className="flex flex-col mb-8 px-4">
								<div className="flex justify-center mt-4">
									<TapAnimationComponent>
										<Button className="mr-4" size="large" onClick={onClose}>
											閉じる
										</Button>

										<Button
											type="primary"
											size="large"
											disabled={checkAttended}
											onClick={handleUpdateAttended}
											loading={registrationUpdateAttendedMutation.isLoading}
										>
											参加
										</Button>
									</TapAnimationComponent>
								</div>
							</div>
						</motion.div>
					</motion.div>
				)}
			</Modal>
		</>
	);
}

ReservationConfirmation.defaultProps = {
	note: [],
};
